import { computed, reactive, ref, watch } from 'vue'
import { useStorage } from '@/stores'

export function useCoinDraft(props) {
  const store = useStorage()

  const local = reactive({
    mint: '', name: '', symbol: '', description: '',
    image: '', website: '', twitter: '', telegram: '',
    lamports: 0, amount: 0, status: '',
  })

  const saving = ref(false)
  const isDirty = ref(false)
  const editingDescription = ref(false)

  const isLaunched = computed(() => {
    const e = props.entity
    if (!e) return false

    const hasTrades = Array.isArray(e.trades) && e.trades.length > 0
    const holdersCount =
      e.holders && typeof e.holders === 'object'
        ? Object.keys(e.holders).length
        : 0

    return hasTrades || holdersCount > 0
  })

  const isOurCoin = computed(() => {
    const e = props.entity
    if (!e?.mint) return false
    if (e.is_own != null || e.isOwn != null) return !!(e.is_own ?? e.isOwn)
    return false
  })

  const canEdit = computed(() => !!props.entity && isOurCoin.value && !isLaunched.value)

  function syncLocalFromEntity(e) {
    if (!e) {
      local.mint = ''
      local.name = ''
      local.symbol = ''
      local.description = ''
      local.image = ''
      local.website = ''
      local.twitter = ''
      local.telegram = ''
      local.lamports = 0
      local.amount = 0
      local.status = ''
      isDirty.value = false
      editingDescription.value = false
      return
    }

    local.mint = e.mint || ''
    local.name = e.name || ''
    local.symbol = e.symbol || ''
    local.description = e.description || ''
    local.image = e.image || ''
    local.website = e.website || ''
    local.twitter = e.twitter || ''
    local.telegram = e.telegram || ''
    local.lamports = e.lamports || 0
    local.amount = e.amount || 0
    local.status = e.status || ''
    isDirty.value = false
    editingDescription.value = false
  }

  function markDirty() {
    if (canEdit.value) isDirty.value = true
  }

  function startEditDescription() {
    if (!canEdit.value) return
    editingDescription.value = true
    isDirty.value = true
  }

  function stopEditDescription() {
    editingDescription.value = false
  }

  function onDescriptionInput() {
    const max = 240
    const text = String(local.description || '')
    if (text.length > max) local.description = text.slice(0, max)
    markDirty()
  }

  async function saveChanges() {
    if (!props.entity?.mint || !isDirty.value || saving.value) return
    saving.value = true

    const payload = {
      name:        String(local.name || '').trim(),
      symbol:      String(local.symbol || '').trim(),
      description: String(local.description || '').trim(),
      image:       String(local.image || '').trim(),
      website:     String(local.website || '').trim(),
      twitter:     String(local.twitter || '').trim(),
      telegram:    String(local.telegram || '').trim(),
    }

    try {
      await store.editCoin(props.entity.mint, payload)
      isDirty.value = false
      editingDescription.value = false
    } catch (e) {
      console.error('coin edit failed:', e)
    } finally {
      saving.value = false
    }
  }

  function resetChanges() {
    if (!props.entity) return
    syncLocalFromEntity(props.entity)
  }

  // sync при смене entity
  watch(
    () => props.entity,
    (next, prev) => {
      if (!next) {
        syncLocalFromEntity(null)
        return
      }

      const prevMint = prev?.mint
      const nextMint = next.mint

      if (!prev || prevMint !== nextMint) {
        syncLocalFromEntity(next)
        return
      }

      if (!isDirty.value && !editingDescription.value) {
        syncLocalFromEntity(next)
      }
    },
    { immediate: true }
  )

  return {
    store,
    local,
    saving,
    isDirty,
    editingDescription,

    isLaunched,
    isOurCoin,
    canEdit,

    syncLocalFromEntity,
    markDirty,

    startEditDescription,
    stopEditDescription,
    onDescriptionInput,

    saveChanges,
    resetChanges,
  }
}
